/**
 * WebSocket Client (Socket.io)
 *
 * Drop-in replacement for SSE_Client with identical event interface.
 * Uses Socket.io for bidirectional real-time communication.
 *
 * @version 1.1.0
 */

// 获取日志控制器（如果存在）
const WS_Logger = (typeof window !== 'undefined' && window.SSE_Logger) 
  ? window.SSE_Logger.create('WS') 
  : { 
      log: () => {}, 
      info: () => {}, 
      warn: () => {}, 
      error: (...args) => console.error('[WS]', ...args) 
    };

/** 与 node-server config.agent.longTaskTimeoutMs 默认一致（12 小时） */
const DEFAULT_LONG_TASK_TIMEOUT_MS = 12 * 60 * 60 * 1000;

class WS_Client {

  constructor(options = {}) {
    this.wsUrl = options.wsUrl || '';
    this.token = options.token || '';
    this.internalToken = options.internalToken || ''; // 内部token用于WP API调用
    this.tokenExpiresAt = 0; // Token过期时间戳
    this.socket = null;
    this.eventHandlers = {};
    this.state = 'CLOSED';
    this.startedAt = null;
    this.sessionId = options.sessionId || this._generateSessionId();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;

    this.aiResponseBuffer = '';
    this.aiTokenCount = 0;
    this.aiChunkCount = 0;
    this.commandOutputBuffer = '';
    
    // Token 自动刷新（在过期前5分钟刷新）
    this._tokenRefreshTimer = null;
  }

  _generateSessionId() {
    return 'ws_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * 检查 token 是否即将过期
   */
  _isTokenExpiringSoon() {
    if (!this.tokenExpiresAt) return true;
    // 提前5分钟认为即将过期
    return Date.now() > (this.tokenExpiresAt - 5 * 60 * 1000);
  }

  /**
   * 解析 JWT token 获取过期时间
   */
  _parseTokenExpiry(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return 0;
      const payload = JSON.parse(atob(parts[1]));
      return (payload.exp || 0) * 1000; // 转换为毫秒
    } catch (e) {
      return 0;
    }
  }

  /**
   * 启动 token 自动刷新定时器
   */
  _startTokenRefreshTimer() {
    this._stopTokenRefreshTimer();
    
    if (!this.tokenExpiresAt) return;
    
    // 在过期前5分钟刷新
    const timeUntilRefresh = Math.max(0, this.tokenExpiresAt - Date.now() - 5 * 60 * 1000);
    
    if (timeUntilRefresh > 0) {
      this._tokenRefreshTimer = setTimeout(async () => {
        try {
          this.token = await this._fetchToken();
          this.tokenExpiresAt = this._parseTokenExpiry(this.token);
          this._refreshSocketAuth();
          this._startTokenRefreshTimer();
        } catch (err) {
          WS_Logger.error('Token auto-refresh failed:', err);
        }
      }, timeUntilRefresh);
    }
  }

  /**
   * 停止 token 自动刷新定时器
   */
  _stopTokenRefreshTimer() {
    if (this._tokenRefreshTimer) {
      clearTimeout(this._tokenRefreshTimer);
      this._tokenRefreshTimer = null;
    }
  }

  /**
   * Obtain a JWT token from WordPress then connect.
   */
  async connect() {
    if (this.state === 'OPEN' || this.state === 'CONNECTING') return;

    this.state = 'CONNECTING';
    this.trigger('connecting');

    try {
      // 检查 token 是否需要刷新
      if (!this.token || this._isTokenExpiringSoon()) {
        this.token = await this._fetchToken();
        this.tokenExpiresAt = this._parseTokenExpiry(this.token);
      }
      
      this._initSocket();
      this._startTokenRefreshTimer();
    } catch (err) {
      WS_Logger.error('Token fetch failed:', err);
      this.state = 'CLOSED';
      this.trigger('error', { type: 'auth_error', message: err.message });
    }
  }

  async _fetchToken() {
    // Build proper REST API URL
    const siteUrl = location.origin;
    const res = await fetch(
      siteUrl + '/wp-json/sse/v1/auth/token',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-WP-Nonce': (window.sseConfig && window.sseConfig.restNonce) || '',
          'Content-Type': 'application/json',
        },
      }
    );
    if (!res.ok) throw new Error('Token request failed: ' + res.status);
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Token generation failed');
    
    // 保存两个token
    this.token = json.data.token;
    this.internalToken = json.data.internal_token || json.data.token;
    
    return json.data.token;
  }

  _initSocket() {
    if (typeof io === 'undefined') {
      WS_Logger.error('Socket.io client not loaded');
      return;
    }

    let wsOrigin = location.origin;
    if (this.wsUrl) {
      try {
        let urlStr = String(this.wsUrl).trim();
        // 页面是 HTTPS 时只允许 WSS：配置里若误写 ws:// 一律升为 wss://（仍由 Nginx 443 终止 TLS）
        if (location.protocol === 'https:' && /^ws:\/\//i.test(urlStr)) {
          urlStr = urlStr.replace(/^ws:\/\//i, 'wss://');
        }
        const u = new URL(urlStr.replace(/^wss:/i, 'https:').replace(/^ws:/i, 'http:'));
        wsOrigin = u.origin;
      } catch (e) {
        WS_Logger.warn('Invalid wsUrl, falling back to origin:', this.wsUrl);
      }
    }
    if (location.protocol === 'https:' && wsOrigin.startsWith('http://')) {
      wsOrigin = 'https://' + wsOrigin.slice('http://'.length);
    }
    if (location.protocol === 'https:' && !wsOrigin.startsWith('https://')) {
      wsOrigin = location.origin;
    }

    this.socket = io(wsOrigin, {
      path: '/ws',
      auth: { 
        token: this.token,
        internal_token: this.internalToken || this.token 
      },
      transports: ['websocket'],
      secure: location.protocol === 'https:' || wsOrigin.startsWith('https:'),
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      this.state = 'OPEN';
      this.startedAt = Date.now();
      this.reconnectAttempts = 0;
      this.trigger('open');
      this.trigger('connected', { session_id: this.sessionId });

      // E2/E5: 重连后请求后台任务对账，并显式恢复当前服务器的前台 Agent。
      const reconnectPayload = this.serverId ? { server_id: this.serverId } : undefined;
      this.socket.emit('get_running_tasks', reconnectPayload);
      this.socket.emit('get_running_operations', reconnectPayload);
      if (this.serverId) {
        this.socket.emit('resume_agent', { server_id: this.serverId });
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.state = 'CLOSED';
      this.trigger('disconnected', { reason });
    });

    this.socket.on('connect_error', (err) => {
      WS_Logger.error('Connection error:', err.message);
      this.reconnectAttempts++;
      this.state = 'RECONNECTING';
      this.trigger('error', { type: 'connection_error', message: err.message });

      if (err.message === 'AUTH_REQUIRED' || err.message === 'AUTH_INVALID') {
        this._recoverAuthAndReconnect().catch((authErr) => {
          this.token = '';
          this.socket.disconnect();
          this.trigger('error', { type: 'auth_error', message: authErr.message || '认证失败，请重新登录' });
        });
      }
    });

    // --- Map server events to the same interface as SSE_Client ---

    const passthrough = [
      'status', 'progress',
      'ai_start', 'ai_chunk', 'ai_complete',
      'command_preview', 'security_check', 'confirmation_required',
      'commands_extracted',
      'execution_start', 'command_output', 'execution_timeout', 'execution_complete',
      'server_error',
      // ReAct Agent events
      'react_iteration', 'agent_iteration',
      'agent_thought', 'thought_chunk',
      'tool_call', 'command_exec', 'tool_result',
      'ai_response', 'final_answer',
      'final_answer_chunk', 'final_answer_done',
      'tool_call_streaming',
      // Background task events（AI 后台任务）
      'task_started', 'task_progress', 'task_polling',
      'task_completed', 'task_failed', 'task_cancelled',
      'task_already_running', 'task_needs_user_input',
      'running_tasks', // E3: 重连后对账后台任务
      'running_operations', 'operation_update', 'operation_auto_resume_prompt',
      'context_usage', 'context_compressed',
      'agent_resumed', // E5: 重连后前台 Agent 重新绑定
      'iteration_warning',
      'agent_stopped', // 修复2：Agent 因明确原因停止
      // Command events
      'command_start', 'command_result', 'command_blocked',
      'pong_server',
      // Script execution events
      'script_started', 'script_output', 'script_completed', 
      'script_failed', 'script_cancelled', 'game_info',
      // Server status events
      'server_status', 'game_info_result',
      // Cloud server events - 云服务器重装相关
      'cloud_status', 'cloud_reinstall_complete', 'cloud_reinstall_failed',
    ];

    passthrough.forEach(event => {
      this.socket.on(event, (data) => {
        // Buffer accumulation (mirrors SSE_Client behaviour)
        // AI 响应不限制行数，AI助手需要完整显示
        if (event === 'ai_chunk') {
          this.aiResponseBuffer += data.content || '';
          this.aiChunkCount = data.chunk_index || this.aiChunkCount + 1;
          data = { ...data, accumulated: this.aiResponseBuffer, chunk_index: this.aiChunkCount };
        }
        // 命令输出：按字符数滚动窗口（保留尾部，避免无限增长导致内存膨胀）
        // 旧逻辑按 30 行截断会丢失大量上下文，改为保留尾部 50000 字符
        if (event === 'command_output') {
          this.commandOutputBuffer += data.output || '';
          const MAX_BUFFER_CHARS = 50000;
          if (this.commandOutputBuffer.length > MAX_BUFFER_CHARS) {
            this.commandOutputBuffer = this.commandOutputBuffer.slice(-MAX_BUFFER_CHARS);
          }
          data = { ...data, accumulated: this.commandOutputBuffer };
        }
        this.trigger(event, data);
      });
    });

    this.socket.on('error', (data) => {
      const msg = data?.message || '';
      const isUserAbort =
        data?.code === 'AGENT_ERROR' && /aborted|abort|用户中止|已中止/i.test(msg);
      if (isUserAbort) {
        WS_Logger.info('Chat aborted by user');
      } else {
        WS_Logger.error('Server error:', data);
      }
      this.trigger('server_error', data);
      this.trigger('error', data);
    });

    this.socket.on('done', (data) => {
      this.trigger('done', data);
    });
  }

  _refreshSocketAuth() {
    if (!this.socket) return;
    this.socket.auth = {
      token: this.token,
      internal_token: this.internalToken || this.token,
    };
    if (this.is_connected()) {
      this.socket.emit('refresh_auth', {
        token: this.token,
        internal_token: this.internalToken || this.token,
      });
    }
  }

  async _recoverAuthAndReconnect() {
    this.token = await this._fetchToken();
    this.tokenExpiresAt = this._parseTokenExpiry(this.token);
    this._refreshSocketAuth();
    this._startTokenRefreshTimer();
    if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
  }

  // ─── Public API (identical to SSE_Client) ──────────────────

  on(event, callback) {
    if (!this.eventHandlers[event]) this.eventHandlers[event] = [];
    this.eventHandlers[event].push(callback);
    return this;
  }

  off(event, callback) {
    if (!this.eventHandlers[event]) return;
    if (callback) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(h => h !== callback);
    } else {
      this.eventHandlers[event] = [];
    }
  }

  once(event, callback) {
    const wrapper = (data) => {
      callback(data, this);
      this.off(event, wrapper);
    };
    this.on(event, wrapper);
  }

  trigger(event, data) {
    const handlers = this.eventHandlers[event];
    if (handlers && handlers.length > 0) {
      handlers.forEach(handler => {
        try { handler(data, this); } catch (e) { WS_Logger.error(`Event handler error (${event}):`, e); }
      });
    }
  }

  close() {
    this._stopTokenRefreshTimer();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.state = 'CLOSED';
    this.token = ''; // 清除 token
    this.tokenExpiresAt = 0;
    this.trigger('disconnected');
  }

  reconnect() {
    this._stopTokenRefreshTimer();
    if (this.socket) this.socket.disconnect();
    this.token = '';
    this.tokenExpiresAt = 0;
    this.state = 'CLOSED';
    this.connect();
  }

  is_connected() {
    return this.socket && this.socket.connected;
  }

  get_state() { return this.state; }
  get_duration() { return this.startedAt ? Math.floor((Date.now() - this.startedAt) / 1000) : 0; }
  get_ai_response() { return this.aiResponseBuffer; }
  get_command_output() { return this.commandOutputBuffer; }
}

/**
 * WS Chat Client — drop-in replacement for SSE_Chat_Client
 */
class WS_Chat_Client extends WS_Client {

  constructor(options = {}) {
    super(options);
    this.serverId = options.serverId || 0;
    this.messageHistory = [];
    this.isProcessing = false;
  }

  async sendMessage(message, options = {}) {
    if (this.isProcessing) {
      console.warn('[WS Chat] Already processing');
      return;
    }

    // Ensure connected
    if (!this.is_connected()) {
      await this.connect();
      await new Promise((resolve) => {
        const check = () => {
          if (this.is_connected()) return resolve();
          setTimeout(check, 100);
        };
        check();
        setTimeout(resolve, 5000); // timeout fallback
      });
    }

    this.isProcessing = true;
    this.aiResponseBuffer = '';
    this.aiChunkCount = 0;
    this.messageHistory.push({ role: 'user', content: message });

    this.socket.emit('chat', {
      server_id: this.serverId,
      message: message,
      session_id: this.sessionId,
    });

    // Listen for completion signals
    const cleanup = () => { this.isProcessing = false; };

    this.once('final_answer', (data) => {
      cleanup();
      if (data && data.answer) {
        this.messageHistory.push({ role: 'assistant', content: data.answer });
      }
    });
    this.once('done', cleanup);
    this.once('server_error', cleanup);
    this.once('error', cleanup);
  }

  executeCommand(command, options = {}) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.commandOutputBuffer = '';

    this.socket.emit('execute_command', {
      server_id: this.serverId,
      command: command,
    });

    this.once('command_result', () => { this.isProcessing = false; });
    this.once('error', () => { this.isProcessing = false; });
  }

  getHistory() { return this.messageHistory; }
  clearHistory() { this.messageHistory = []; }

  /**
   * 获取服务器级别的对话历史（跨设备同步）
   * @returns {Promise<Array>} 对话历史数组
   */
  async getServerHistory() {
    // Ensure connected
    if (!this.is_connected()) {
      await this.connect();
      await new Promise((resolve) => {
        const check = () => {
          if (this.is_connected()) return resolve();
          setTimeout(check, 100);
        };
        check();
        setTimeout(resolve, 5000); // timeout fallback
      });
    }

    if (!this.is_connected()) {
      console.warn('[WS Chat] Not connected, cannot get history');
      return [];
    }

    return new Promise((resolve) => {
      const onHistory = (data) => {
        this.off('server_history', onHistory);
        if (data && data.server_id === this.serverId) {
          this.trigger('context_usage', data.contextUsage || {
            server_id: data.server_id,
            usedBytes: 0,
            source: 'server_session',
            timestamp: Date.now(),
          });
          resolve(data.history || []);
        } else {
          resolve([]);
        }
      };

      this.on('server_history', onHistory);
      this.socket.emit('get_server_history', { server_id: this.serverId });

      // Timeout fallback
      setTimeout(() => {
        this.off('server_history', onHistory);
        resolve([]);
      }, 5000);
    });
  }

  /**
   * 清除服务器级别的对话历史（跨设备同步）
   */
  clearServerHistory() {
    if (this.socket && this.is_connected()) {
      this.socket.emit('clear_session', { server_id: this.serverId });
      this.messageHistory = [];
    }
  }
}

// Export to global
window.WS_Client = WS_Client;
window.WS_Chat_Client = WS_Chat_Client;

/**
 * WS Script Client — 脚本执行WebSocket客户端
 * 支持实时进度推送，替代HTTP轮询
 */
class WS_Script_Client extends WS_Client {

  constructor(options = {}) {
    super(options);
    this.currentTaskId = null;
    this.scriptOutputBuffer = '';
    this.isExecuting = false;
    /** @type {ReturnType<typeof setTimeout>|null} */
    this._executeScriptTimeoutId = null;
    /** 输出缓冲区最大行数，防止内存膨胀 */
    this.maxBufferLines = 50;
  }

  /**
   * 执行脚本
   * @param {object} options 执行选项
   * @param {number} options.script_id 脚本ID
   * @param {number} options.server_id 服务器ID（与server_info二选一）
   * @param {object} options.server_info 直接提供服务器信息（与server_id二选一）
   * @param {object} options.params 脚本参数 { username, dynamic_code, ... }
   * @param {boolean} [options.full_auto_reinstall] 为 true 时跳过 /www 预检（云服务器全自动重装后的执行）
   * @returns {Promise<object>} 执行结果
   */
  async executeScript(options = {}) {
    const { script_id, server_id, server_info, params = {} } = options;

    if (!script_id) {
      throw new Error('缺少脚本ID');
    }

    if (!server_id && !server_info) {
      throw new Error('缺少服务器信息');
    }

    // 确保已连接
    if (!this.is_connected()) {
      await this.connect();
      await new Promise((resolve) => {
        const check = () => {
          if (this.is_connected()) return resolve();
          setTimeout(check, 100);
        };
        check();
        setTimeout(resolve, 5000);
      });
    }

    if (!this.is_connected()) {
      throw new Error('WebSocket连接失败');
    }

    if (this._executeScriptTimeoutId) {
      clearTimeout(this._executeScriptTimeoutId);
      this._executeScriptTimeoutId = null;
    }

    // 重置状态
    this.scriptOutputBuffer = '';
    this.isExecuting = true;

    return new Promise((resolve, reject) => {
      // 监听脚本启动响应
      const onStart = (data) => {
        if (data.scriptId === script_id) {
          this.currentTaskId = data.taskId;
          // 注意：不要在这里调用 trigger，因为 passthrough 已经处理了
        }
      };

      // 监听脚本输出
      const onOutput = (data) => {
        if (data.taskId === this.currentTaskId) {
          this.scriptOutputBuffer += data.output || '';
          // 限制缓冲区大小，防止内存膨胀
          this.scriptOutputBuffer = this._truncateBuffer(this.scriptOutputBuffer);
        }
      };

      // 监听脚本完成
      const onCompleted = (data) => {
        if (data.taskId === this.currentTaskId) {
          if (this._executeScriptTimeoutId) {
            clearTimeout(this._executeScriptTimeoutId);
            this._executeScriptTimeoutId = null;
          }
          this.isExecuting = false;
          this.off('script_started', onStart);
          this.off('script_output', onOutput);
          this.off('script_completed', onCompleted);
          this.off('script_failed', onFailed);
          this.off('error', onError);

          resolve({
            success: data.success,
            exitCode: data.exitCode,
            duration: data.duration,
            output: this.scriptOutputBuffer,
            gameInfo: data.gameInfo,
          });
        }
      };

      // 监听脚本失败
      const onFailed = (data) => {
        if (data.taskId === this.currentTaskId) {
          if (this._executeScriptTimeoutId) {
            clearTimeout(this._executeScriptTimeoutId);
            this._executeScriptTimeoutId = null;
          }
          this.isExecuting = false;
          this.off('script_started', onStart);
          this.off('script_output', onOutput);
          this.off('script_completed', onCompleted);
          this.off('script_failed', onFailed);
          this.off('error', onError);

          reject(new Error(data.message || '脚本执行失败'));
        }
      };

      // 监听错误
      const onError = (data) => {
        if (this._executeScriptTimeoutId) {
          clearTimeout(this._executeScriptTimeoutId);
          this._executeScriptTimeoutId = null;
        }
        this.isExecuting = false;
        this.off('script_started', onStart);
        this.off('script_output', onOutput);
        this.off('script_completed', onCompleted);
        this.off('script_failed', onFailed);
        this.off('error', onError);

        reject(new Error(data.message || 'WebSocket错误'));
      };

      // 注册事件监听
      this.on('script_started', onStart);
      this.on('script_output', onOutput);
      this.on('script_completed', onCompleted);
      this.on('script_failed', onFailed);
      this.on('error', onError);

      // 发送执行请求（full_auto_reinstall：云服务器全自动重装后的执行，跳过 /www 预检）
      this.socket.emit('execute_script', {
        script_id,
        server_id,
        server_info,
        params,
        full_auto_reinstall: options.full_auto_reinstall === true,
      });

      // 设置超时
      const timeout = options.timeout || DEFAULT_LONG_TASK_TIMEOUT_MS;
      this._executeScriptTimeoutId = setTimeout(() => {
        this._executeScriptTimeoutId = null;
        if (this.isExecuting) {
          this.cancelScript();
          reject(new Error('脚本执行超时'));
        }
      }, timeout);
    });
  }

  /**
   * 取消脚本执行
   */
  async cancelScript() {
    if (!this.currentTaskId) return;

    return new Promise((resolve) => {
      const onCancelled = (data) => {
        if (data.taskId === this.currentTaskId) {
          this.isExecuting = false;
          this.off('script_cancelled', onCancelled);
          resolve(data);
        }
      };

      this.on('script_cancelled', onCancelled);
      this.socket.emit('cancel_script', { task_id: this.currentTaskId });

      // 超时处理
      setTimeout(() => {
        this.off('script_cancelled', onCancelled);
        resolve({ success: false, message: '取消请求超时' });
      }, 5000);
    });
  }

  /**
   * 获取任务状态
   * @param {string} taskId 任务ID
   * @returns {Promise<object>} 任务状态
   */
  async getTaskStatus(taskId) {
    return new Promise((resolve) => {
      const onStatus = (data) => {
        if (data.id === taskId || data.taskId === taskId) {
          this.off('task_status', onStatus);
          resolve(data);
        }
      };

      this.on('task_status', onStatus);
      this.socket.emit('get_task_status', { task_id: taskId });

      setTimeout(() => {
        this.off('task_status', onStatus);
        resolve({ found: false, error: '获取状态超时' });
      }, 5000);
    });
  }

  /**
   * 获取累积输出
   */
  getOutput() {
    return this.scriptOutputBuffer;
  }

  /**
   * 限制输出缓冲区的最大行数
   * @param {string} buffer 输出缓冲区内容
   * @returns {string} 截断后的缓冲区内容
   */
  _truncateBuffer(buffer) {
    if (!buffer || buffer.length < 5000) return buffer;
    
    const lines = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    if (lines.length <= this.maxBufferLines) return buffer;
    
    const omitted = lines.length - this.maxBufferLines;
    const truncated = lines.slice(-this.maxBufferLines);
    return '… 已省略上方 ' + omitted + ' 行，以下为最近 ' + this.maxBufferLines + ' 行 …\n' + truncated.join('\n');
  }

  /**
   * 是否正在执行
   */
  isRunning() {
    return this.isExecuting;
  }

  /**
   * 检查服务器状态
   * @param {object} options 选项
   * @param {number} options.server_id 服务器ID
   * @param {object} options.server_info 直接提供服务器信息
   * @returns {Promise<object>} 状态结果
   */
  async checkServerStatus(options = {}) {
    const { server_id, server_info } = options;

    if (!server_id && !server_info) {
      throw new Error('缺少服务器信息');
    }

    // 确保已连接（轮询恢复任务时页面可能刚唤醒，不能 5s 就放弃）
    if (!this.is_connected()) {
      await this.connect();
      await new Promise((resolve, reject) => {
        const deadline = Date.now() + 20000;
        const t = setInterval(() => {
          if (this.is_connected()) {
            clearInterval(t);
            resolve();
          } else if (Date.now() > deadline) {
            clearInterval(t);
            reject(new Error('WebSocket 未连接，请刷新页面'));
          }
        }, 100);
      });
    }

    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket 未连接，请刷新页面');
    }

    return new Promise((resolve, reject) => {
      const onStatus = (data) => {
        this.off('server_status', onStatus);
        if (data.success) {
          resolve({
            status: data.status,
            message: data.message,
            gameInfo: data.gameInfo,
            scriptLog: data.scriptLog || '',
          });
        } else {
          reject(new Error(data.message || '状态检测失败'));
        }
      };

      this.on('server_status', onStatus);
      this.socket.emit('check_server_status', { server_id, server_info });

      // 超时处理
      setTimeout(() => {
        this.off('server_status', onStatus);
        reject(new Error('状态检测超时'));
      }, 30000);
    });
  }

  /**
   * 获取游戏信息
   * @param {object} options 选项
   * @param {number} options.server_id 服务器ID
   * @param {object} options.server_info 直接提供服务器信息
   * @returns {Promise<string|null>} 游戏信息
   */
  async getGameInfo(options = {}) {
    const { server_id, server_info } = options;

    if (!server_id && !server_info) {
      throw new Error('缺少服务器信息');
    }

    if (!this.is_connected()) {
      await this.connect();
      await new Promise((resolve, reject) => {
        const deadline = Date.now() + 20000;
        const t = setInterval(() => {
          if (this.is_connected()) {
            clearInterval(t);
            resolve();
          } else if (Date.now() > deadline) {
            clearInterval(t);
            reject(new Error('WebSocket 未连接，请刷新页面'));
          }
        }, 100);
      });
    }

    if (!this.socket || !this.socket.connected) {
      throw new Error('WebSocket 未连接，请刷新页面');
    }

    return new Promise((resolve, reject) => {
      const onResult = (data) => {
        this.off('game_info_result', onResult);
        if (data.success) {
          resolve(data.gameInfo);
        } else {
          resolve(null);
        }
      };

      this.on('game_info_result', onResult);
      this.socket.emit('get_game_info', { server_id, server_info });

      // 超时处理
      setTimeout(() => {
        this.off('game_info_result', onResult);
        resolve(null);
      }, 15000);
    });
  }
}

window.WS_Script_Client = WS_Script_Client;
